import { inject, Injectable, signal } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly oidc = inject(OidcSecurityService);

    /** True once a valid token is present for the session. */
    readonly isAuthenticated = signal(false);

    /** Guards against issuing more than one /connect/authorize per page load
        (route redirects can run the auth guard multiple times, which would
        generate multiple nonces and break the callback with "incorrect nonce"). */
    private authorizeTriggered = false;

    /** Run once at app start; processes the auth-code callback if present. */
    async checkAuth(): Promise<boolean> {
        try {
            const { isAuthenticated } = await firstValueFrom(this.oidc.checkAuth());
            this.isAuthenticated.set(isAuthenticated);
            return isAuthenticated;
        } catch {
            // Authority unreachable (e.g. backend not running) — treat as signed-out.
            this.isAuthenticated.set(false);
            return false;
        }
    }

    login(): void {
        if (this.authorizeTriggered) {
            return;
        }
        this.authorizeTriggered = true;
        this.oidc.authorize();
    }

    logout(): void {
        this.oidc.logoff().subscribe();
    }

    async getAccessToken(): Promise<string> {
        return firstValueFrom(this.oidc.getAccessToken());
    }
}
